import { useCallback, useState } from 'react'
import { RecurlyError } from '@recurly/recurly-js'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { CheckoutResponseError, PaymentMethodType, PaymentMethods } from '@pff-consumer/schema'
import { useAnalytics } from '@pff-consumer/analytics'
import { setCheckoutSessionData, setSessionId, transformCheckoutResponseToCheckoutSession } from '@pff-consumer/utils'
import { useCheckoutState } from './useCheckoutState'
import { useRedirectTo } from './useRedirectTo'

const { useCheckoutActions, useNewsLetterPreference } = checkoutHooks

const COMPLETE_ROUTE = '/complete'

/*
 * Need to make sure the protocol is added to the url because we
 * need to make sure we are navigating back to pff/pffstaging
 */
const formatUrl = (url: string): string => {
  return url.includes('https://') || url.includes('http://') ? url : `https://${url}`
}

/**
 * This hook holds the different callbacks and state needed for the one-step-checkout
 * payment methods.
 */
export const usePaymentMethodState = () => {
  const { validateEmail, updatePaymentError, updateCheckoutError } = useCheckoutActions()
  const { isEmailValid, plan, email, promoCode, checkout, isLoading, variant } = useCheckoutState()
  const router = useRouter()
  const [isSuccessful, setIsSuccessful] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('')
  const { redirectTo } = useRedirectTo({ router })
  const shouldSubscribeToNewsletter = useNewsLetterPreference()

  const { addAnalyticsUser } = useAnalytics()

  const onComplete = useCallback(
    (recurlyToken: string, paymentType: string, recaptchaToken: string) => {
      checkout({
        recurlyToken,
        planCode: plan,
        email,
        promoCode,
        paymentType,
        recaptchaToken,
        variant,
        shouldSubscribeToNewsletter,
      }).then((response) => {
        setPaymentMethod(PaymentMethods.DEFAULT)
        // Handle Error
        if (response?.error) {
          Sentry.captureException(new Error(JSON.stringify(response.error)))

          // Known error
          if ('data' in response.error) {
            updateCheckoutError(response.error.data as CheckoutResponseError)
          }
          // unknown error
          else {
            throw new Error('Error with Checkout Response') // Should be caught by error boundary
          }
        }
        // Handle Data
        if (response?.data) {
          const { data } = response
          // There is a chance that an error is in the data
          if ('error' in data) {
            Sentry.captureException(new Error(JSON.stringify(data)))
            updateCheckoutError(data)
          }
          // Happy Path, yay
          else {
            const checkoutSessionData = transformCheckoutResponseToCheckoutSession(data, email)
            const urlParams = new URLSearchParams(window.location.search)
            const completeUrl = redirectTo ? `${COMPLETE_ROUTE}?redirect_to=${formatUrl(redirectTo)}` : COMPLETE_ROUTE

            setIsSuccessful(true)
            setCheckoutSessionData(checkoutSessionData)
            setSessionId(urlParams.get('session'))

            // Will add the use to analytics
            addAnalyticsUser({ uid: data.uid, email })
            // We want to use replace instead of push so that the user can't navigate back after successful payment
            router.replace(completeUrl)
          }
        }
      })
    },
    [
      checkout,
      plan,
      email,
      promoCode,
      updateCheckoutError,
      addAnalyticsUser,
      router,
      redirectTo,
      variant,
      shouldSubscribeToNewsletter,
    ]
  )

  const onError = useCallback(
    // RecurlyError should be an Error instance not an object, but based on Sentry reporting
    // we can tell it is sometimes an error instance and sometimes a javascript object
    (error: RecurlyError) => {
      console.error('Error: ', error)

      // updatePaymentError sets an error in our RTK store
      // because of this it must be a serializable object and not an Error instance
      const serializableError = {
        name: error.name,
        code: error.code,
        message: error.message,
        classification: error.classification,
        help: error.help,
        fields: error.fields,
        details: error.details,
      }
      updatePaymentError(serializableError)

      Sentry.withScope((scope) => {
        scope.setExtras(serializableError)
        // this check is for just in case Recurly sends us a javascript object
        // if we try to call captureException with a regular object we get junk error reporting
        if (error instanceof Error) {
          Sentry.captureException(error)
        } else {
          Sentry.captureException(JSON.stringify(serializableError))
        }
      })

      setPaymentMethod(PaymentMethods.DEFAULT)
    },
    [updatePaymentError]
  )

  return {
    isEmailValid,
    onComplete,
    isOnCompletePending: isLoading || isSuccessful || paymentMethod !== PaymentMethods.DEFAULT,
    onDisabledClick: validateEmail, //
    onError,
    onRecurlyTokenInit: setPaymentMethod,
    isApplePayProcessing: paymentMethod === PaymentMethods.APPLE_PAY,
    isPayPalProcessing: paymentMethod === PaymentMethods.PAYPAL,
    isCreditCardProcessing: paymentMethod === PaymentMethods.CREDIT_CARD,
  }
}
